import { FadeIn, SectionLabel } from "@/components/Shared";
import { STATS } from "@/lib/data";
import { Eye, Target, Heart, ShieldCheck, Check, Recycle, Package, Palette, Leaf, Sparkles } from "lucide-react";
import aboutStoryImage from "@/assets/about/About-ThePacfullystory.jpeg";

export default function About() {
  const missionPillars = [
    { k: "Unify", d: "One company. Every packaging profile. From design to delivery." },
    { k: "Organize", d: "World-class manufacturing that raises the bar for reliability, consistency, operational efficiency and professionalism." },
    { k: "Digitize", d: "A fully digital, paperless plant — technology driven using IoT, MES, ERP and AI." },
    { k: "Democratize", d: "Premium, customizable packaging made accessible to every business, every consumer, everywhere." },
    { k: "Educate", d: "Building the next generation of printing and packaging technicians and professionals." },
  ];

  const pillars = [
    { i: Heart, t: "Why we exist", d: "Because most brands settle for packaging that arrives. We craft packaging that's remembered." },
    { i: ShieldCheck, t: "Commitment", d: "ISO 9001 quality, FSC-certified materials, food-grade inks and on-time delivery as our default." },
  ];

  return (
    <div data-testid="about-page">
      <section className="pt-32 md:pt-44 pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <SectionLabel>The Pacfully story</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-4 leading-[1.05]">Packaging that leaves<br />a lasting impression.</h2>
            <p className="mt-6 text-white/65 leading-relaxed">
              At <span className="text-white">Pacfully</span>, we manufacture packaging that makes a lasting impression. Specializing in rigid boxes, monocartons, printed corrugated boxes and paper bags, we bring precision craftsmanship and creative excellence to every product we produce — serving a diverse range of industries with solutions built to perform and designed to stand out.
            </p>
            <p className="mt-4 text-white/65 leading-relaxed">
              Sustainability is not a trend for us — it is our foundation. Every material we select and every process we employ reflects our unwavering commitment to eco-conscious manufacturing, ensuring that your packaging is as responsible as it is refined.
            </p>
            <p className="mt-4 text-white/65 leading-relaxed">
              We believe great packaging does more than protect — it communicates. It carries your brand's identity, captures attention and creates a connection with your customer before a single word is read. Our fully customizable designs are crafted to give your brand a distinct voice in a competitive marketplace.
            </p>
            <p className="mt-4 text-white/65 leading-relaxed">
              Whether you require a specialized short run or a large-scale production order, Pacfully delivers with the same commitment to quality, flexibility and precision — every single time. We are not just a manufacturer; we are a long-term partner invested in the success of your brand.
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="relative h-[480px] rounded-3xl overflow-hidden neo-card">
              <img src={aboutStoryImage} alt="Pacfully premium packaging" className="w-full h-full object-cover" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="py-20 md:py-28 bg-ink-800/30 border-y border-white/5" data-testid="vision-mission-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeIn>
            <SectionLabel>What drives us</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mt-4 max-w-3xl leading-[1.05]">Vision &amp; Mission.</h2>
          </FadeIn>
        </div>

        {/* MANIFESTO MARQUEE — six mission verbs */}
        <div className="relative mt-14 md:mt-16 py-8 border-y border-white/5 bg-ink-900/30 overflow-hidden" data-testid="manifesto-marquee">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-ink-900 to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-ink-900 to-transparent z-10" />
          <div className="flex animate-marquee-fast whitespace-nowrap gap-14 md:gap-20">
            {(() => {
              const verbs = ["Unify", "Organize", "Digitize", "Democratize", "Educate", "Innovate"];
              const cycles = ["a", "b", "c"];
              const repeatedVerbs = cycles.flatMap((cycle) =>
                verbs.map((verb) => ({ id: `${verb}-${cycle}`, value: verb }))
              );

              return repeatedVerbs.map((entry) => (
                <div
                  key={entry.id}
                  data-testid={`manifesto-verb-${entry.id}`}
                  className="shrink-0 flex items-center gap-8 md:gap-14"
                >
                  <span className={`font-display tracking-tight text-5xl md:text-7xl lg:text-8xl leading-none ${entry.id.includes("Unify") || entry.id.includes("Digitize") || entry.id.includes("Educate") ? "text-white" : "text-brand"}`}>
                    {entry.value}.
                  </span>
                  <span className="text-white/25 text-5xl md:text-7xl lg:text-8xl leading-none">·</span>
                </div>
              ));
            })()}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16 md:mt-20">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* VISION */}
            <FadeIn>
              <div className="relative h-full neo-card rounded-3xl p-8 md:p-12 overflow-hidden" data-testid="vision-card">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand/10 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-brand/15 border border-brand/30 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-brand" />
                    </div>
                    <p className="text-xs uppercase tracking-[0.3em] text-white/40">Vision</p>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-medium mt-6 leading-tight">Engineered packaging, born in India, built for the world.</h3>
                  <div className="mt-6 space-y-5 text-white/70 leading-relaxed">
                    <p>
                      To become the most prominent printing and packaging organization — born in India, built for the world — by transforming a fragmented industry into an organized, systematic, knowledge-powered, technology-driven ecosystem where packaging is <span className="text-white">engineered, not just produced</span>.
                    </p>
                    <p>
                      We envision a future where <span className="text-brand font-medium">Pacfully</span> is synonymous with the entire spectrum of packaging and labelling — from rigid boxes, mono-cartons, printed corrugated boxes and paper bags today, to labels, pouches, tetra packs, paper-fibre moulding and beyond tomorrow — offering end-to-end solutions from design to delivery, across every industry, every customer type and every business model.
                    </p>
                    <p>
                      We believe packaging is not a commodity — it is an <span className="text-white">engineering discipline</span>. A future where every box, every carton, every label is purposefully designed, precisely manufactured and powerfully positioned as a tool for sustainability, brand storytelling and business growth.
                    </p>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* MISSION */}
            <FadeIn delay={0.1}>
              <div className="relative h-full neo-card rounded-3xl p-8 md:p-12 overflow-hidden" data-testid="mission-card">
                <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-brand/10 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-brand/15 border border-brand/30 flex items-center justify-center">
                      <Target className="w-5 h-5 text-brand" />
                    </div>
                    <p className="text-xs uppercase tracking-[0.3em] text-white/40">Mission</p>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-medium mt-6 leading-tight">Solving packaging's biggest challenges — systematically and boldly.</h3>
                  <p className="mt-6 text-white/70 leading-relaxed">
                    Pacfully exists to solve the packaging industry's most pressing challenges — systematically and boldly.
                  </p>
                  <p className="mt-3 text-white/85 leading-relaxed font-medium">
                    To Unify. Organize. Digitize. Democratize. Educate. Innovate. And Grow.
                  </p>

                  <ul className="mt-8 space-y-4" data-testid="mission-pillars">
                    {missionPillars.map((m, i) => (
                      <li key={m.k} className="flex gap-4" data-testid={`mission-pillar-${m.k.toLowerCase()}`}>
                        <div className="shrink-0 mt-1">
                          <div className="w-7 h-7 rounded-lg bg-brand/15 border border-brand/30 flex items-center justify-center">
                            <Check className="w-3.5 h-3.5 text-brand" />
                          </div>
                        </div>
                        <div>
                          <p className="font-display text-lg text-white">To {m.k}</p>
                          <p className="text-sm text-white/60 leading-relaxed mt-0.5">{m.d}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Secondary pillars */}
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {pillars.map((p, i) => (
              <FadeIn key={p.t} delay={i * 0.06}>
                <div className="neo-card rounded-3xl p-8 md:p-10" data-testid={`pillar-${p.t.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-12 h-12 rounded-xl bg-brand/15 border border-brand/30 flex items-center justify-center">
                    <p.i className="w-5 h-5 text-brand" />
                  </div>
                  <h3 className="font-display text-2xl font-medium mt-6">{p.t}</h3>
                  <p className="text-white/60 mt-3 leading-relaxed">{p.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-ink-800/30 border-y border-white/5" data-testid="sustainability-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeIn>
            <SectionLabel>Sustainable in every box</SectionLabel>
            <div className="mt-4 flex flex-col md:flex-row md:items-end justify-between gap-6">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-2xl leading-[1.05]">
                Boxes that are <span className="text-brand">Pacfully</span> made.
              </h2>
              <p className="text-white/70 max-w-md leading-relaxed">
                We believe premium packaging and responsible manufacturing can go hand in hand. Our focus is on designing boxes that balance aesthetics, functionality and material efficiency — while supporting our customers' sustainability goals.
              </p>
            </div>
          </FadeIn>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Recycle, title: "Recyclable Paper-Based Materials", desc: "Designed using paperboard and other recyclable materials wherever the application permits." },
              { icon: Package, title: "Optimized Material Usage", desc: "Structural designs that minimize material waste without compromising strength or quality." },
              { icon: Palette, title: "Responsible Production", desc: "Precision manufacturing and colour proofing help reduce trial runs and production waste." },
              { icon: Leaf, title: "Built for a Circular Economy", desc: "Packaging solutions developed with recyclability and long product life in mind." },
            ].map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.06}>
                <div
                  data-testid={`sustainability-card-${i}`}
                  className="group relative h-full neo-card rounded-3xl p-7 md:p-8 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:border-brand/30 hover:shadow-[0_0_40px_rgba(244,90,42,0.12)]"
                >
                  <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full bg-brand/10 blur-3xl opacity-0 group-hover:opacity-100 transition duration-700 pointer-events-none" />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-2xl bg-brand/15 border border-brand/30 flex items-center justify-center transition group-hover:bg-brand">
                      <s.icon className="w-5 h-5 text-brand group-hover:text-white transition" />
                    </div>
                    <h3 className="font-display text-xl font-medium mt-6 leading-tight">{s.title}</h3>
                    <p className="mt-3 text-sm text-white/65 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Small highlight banner */}
          <FadeIn delay={0.1}>
            <div className="mt-14 relative rounded-[2rem] overflow-hidden neo-card p-8 md:p-12" data-testid="sustainability-banner">
              <div className="absolute -top-20 -right-20 w-[380px] h-[380px] rounded-full bg-brand/15 blur-[140px] pointer-events-none" />
              <div className="absolute -bottom-16 -left-16 w-[320px] h-[320px] rounded-full bg-[#4ade80]/10 blur-[140px] pointer-events-none" />
              <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                <div className="shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-brand/15 border border-brand/30 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 md:w-7 md:h-7 text-brand" />
                </div>
                <div className="flex-1">
                  <p className="font-display text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-[1.15]">
                    Creating premium packaging that's designed not only to impress — but also with greater consideration for the environment.
                  </p>
                  <p className="mt-4 text-brand text-sm md:text-base font-medium tracking-wide">— Engineering a Greener Future</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeIn>
            <SectionLabel>By the numbers</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-4 max-w-2xl leading-[1.05]">A decade of measurable craft.</h2>
          </FadeIn>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <FadeIn key={s.label} delay={i * 0.05}>
                <div className="neo-card rounded-3xl p-8">
                  <p className="font-display text-5xl md:text-6xl font-medium text-brand leading-none">
                    {s.value}{s.suffix}
                    {s.unit && <span className="ml-2 text-xl md:text-2xl font-medium text-brand/85"> {s.unit}</span>}
                  </p>
                  <p className="text-xs uppercase tracking-[0.25em] text-white/40 mt-3">{s.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
